import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { putAuth } from '@/api/ApiCalls';
import { LandingSiteFormDtoIn } from '@/models/in/LandingSiteFormDtoIn';
import PopupDialog from './PopupDialog';
import { LANDING_SITE_FORMS_URL } from './landingSiteForms';
import styles from './LandingSiteFormsPage.module.scss';

interface EditLinkDialogProps {
  form: LandingSiteFormDtoIn;
  onClose: () => void;
  onSuccess: () => void;
}

const EditLinkDialog = ({ form, onClose, onSuccess }: EditLinkDialogProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();

  // A link entry always has an address; the empty string only stands in for the null the shared
  // entry type allows, which belongs to the kinds this dialog is never opened for.
  const [url, setUrl] = useState(form.url ?? '');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const save = async () => {
    const trimmed = url.trim();

    if (!/^https?:\/\/.+/i.test(trimmed)) {
      setError(t('landingSiteForms.dialog.invalidUrl'));
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      await putAuth(
        `${LANDING_SITE_FORMS_URL}/${form.key}/link`,
        { Authorization: `Bearer ${currentUser?.token}` },
        {},
        { url: trimmed }
      );
      onSuccess();
      onClose();
    } catch {
      setError(t('landingSiteForms.dialog.saveError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PopupDialog title={t('landingSiteForms.dialog.editLinkTitle', { name: t(`landingSiteForms.items.${form.key}`) })} onClose={onClose}>
      <div className={styles.dialogBody}>
        <Input
          value={url}
          dir="ltr"
          onChange={(e) => { setUrl(e.target.value); setError(null); }}
          placeholder="https://"
          error={!!error}
        />

        <p className={`body-sm-regular ${styles.dialogWarning}`}>{t('landingSiteForms.dialog.publishWarning')}</p>

        {error && <p className={`body-sm-regular ${styles.error}`}>{error}</p>}

        <div className={styles.dialogActions}>
          <Button variant="white" onClick={onClose} disabled={isSubmitting}>{t('cancel')}</Button>
          <Button variant="primary" onClick={save} disabled={isSubmitting || url.trim() === form.url}>
            {isSubmitting ? t('saving') : t('landingSiteForms.dialog.publish')}
          </Button>
        </div>
      </div>
    </PopupDialog>
  );
};

export default EditLinkDialog;
